import React, { useEffect, useState } from 'react';
import CreateTier from './Submenu/CreateTier';
import TierList from './Submenu/TierList';
import TierLogs from './Submenu/TierLogs';
import {
    getCurrenciesList,
    getTierLists,
    getTierTypesList,
    getTierLogs as fetchTierLogs,
} from '../../../services/pricingService';
import { getVenueTypesList } from '../../../services/venueTypesService';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const Pricing = ({ isActive }) => {
    const dispatch = useDispatch();
    const [isActiveMenu, setIsActiveMenu] = useState('tierList');
    const [isLoading, setIsLoading] = useState(false);
    const [tierList, setTierList] = useState([]);
    const [forceRerender, setForceRerender] = useState(false);
    const [screenPrices, setScreenPrices] = useState(null);
    const [tierTypes, setTierTypes] = useState(null);
    const [venueTypes, setVenueTypes] = useState([]);
    const [tierLogs, setTierLogs] = useState([]);
    const [logsVersion, setLogsVersion] = useState(0);
    const currencies = useSelector((state) => {
        return state.wallet.currencies;
    });
    const [currenciesWithLabel, setCurrenciesWithLabel] = useState([]);
    const [selectedCurrency, setSelectedCurrency] = useState(null);
    const { t } = useTranslation();

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const [
                    tierListResponse,
                    currenciesResponse,
                    tierTypesResponse,
                    venueTypesResponse,
                ] = await Promise.all([
                    getTierLists(),
                    getCurrenciesList(),
                    getTierTypesList(),
                    getVenueTypesList(),
                ]);
                setTierList(tierListResponse.message);
                setScreenPrices(currenciesResponse.data.conversion_rates);
                setTierTypes(tierTypesResponse.message);
                setVenueTypes(venueTypesResponse);
            } catch (error) {
                setTierList([]);
                setScreenPrices(null);
                setTierTypes(null);
                setVenueTypes([]);
                // eslint-disable-next-line no-console
                console.error('Failed to load pricing data', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [forceRerender]);

    useEffect(() => {
        setCurrenciesWithLabel(
            currencies.map((currency) => {
                return {
                    ...currency,
                    label: `${currency.symbol} ${currency.short_name} - ${currency.name}`,
                    value: currency.short_name,
                };
            })
        );
    }, [currencies]);

    const handleChangeCurrency = (currency) => {
        if (currency.value === 'SOL') {
            setSelectedCurrency({
                label: currency.label,
                value: 'PEN',
            });
        } else {
            setSelectedCurrency(currency);
        }
    };

    const handleFinancialMenu = (section) => {
        setIsActiveMenu(section);
        setForceRerender(!forceRerender);
    };

    const handleTierLogCreated = () => {
        setLogsVersion((version) => version + 1);
    };

    useEffect(() => {
        let mounted = true;
        const fetchLogs = async () => {
            try {
                const response = await fetchTierLogs();
                if (!mounted) return;
                setTierLogs(response?.message || []);
            } catch (error) {
                if (!mounted) return;
                setTierLogs([]);
                // eslint-disable-next-line no-console
                console.error('Failed to load tier logs', error);
            }
        };

        fetchLogs();
        return () => {
            mounted = false;
        };
    }, [logsVersion]);

    return (
        <div
            className={`${
                isActive ? 'block' : 'hidden'
            } bg-white bg-opacity-90 rounded-b-md shadow-lg p-4`}>
            <div className="flex divide-x divide-dotted">
                <div className="w-50">
                    <nav className="flex flex-col">
                        <button
                            id="tierList"
                            onClick={() => {
                                handleFinancialMenu('tierList');
                            }}
                            className={`${
                                isActiveMenu === 'tierList'
                                    ? 'tabActiveV font-semibold truncate'
                                    : ''
                            } tabGeneral uppercase text-xs text-right whitespace-nowrap`}>
                            <span className="hidden md:inline-block">
                                {t(
                                    'sections.management.section.pricing.tierList.tab'
                                )}
                            </span>
                        </button>
                        <button
                            id="createTier"
                            onClick={() => {
                                handleFinancialMenu('createTier');
                            }}
                            className={`${
                                isActiveMenu === 'createTier'
                                    ? 'tabActiveV font-semibold truncate'
                                    : ''
                            } tabGeneral uppercase text-xs text-right whitespace-nowrap`}>
                            <span className="hidden md:inline-block">
                                {t(
                                    'sections.management.section.pricing.createTier.tab'
                                )}
                            </span>
                        </button>
                        <button
                            id="tierLogs"
                            onClick={() => {
                                handleFinancialMenu('tierLogs');
                            }}
                            className={`${
                                isActiveMenu === 'tierLogs'
                                    ? 'tabActiveV font-semibold truncate'
                                    : ''
                            } tabGeneral uppercase text-xs text-right whitespace-nowrap`}>
                            <span className="hidden md:inline-block">
                                {t(
                                    'sections.management.section.pricing.tierLogs.tab'
                                )}
                            </span>
                        </button>
                    </nav>
                </div>

                <CreateTier
                    isActive={isActiveMenu === 'createTier'}
                    rerender={forceRerender}
                    screenPrices={screenPrices}
                    currenciesWithLabel={currenciesWithLabel}
                    selectedCurrency={selectedCurrency}
                    handleChangeCurrency={handleChangeCurrency}
                    venueTypes={venueTypes}
                    onTierLogCreated={handleTierLogCreated}
                />
                <TierList
                    isActive={isActiveMenu === 'tierList'}
                    isLoading={isLoading}
                    tierList={tierList}
                    setTierList={setTierList}
                    dispatch={dispatch}
                    screenPrices={screenPrices}
                    currenciesWithLabel={currenciesWithLabel}
                    selectedCurrency={selectedCurrency}
                    handleChangeCurrency={handleChangeCurrency}
                    venueTypes={venueTypes}
                    onTierLogCreated={handleTierLogCreated}
                />
                <TierLogs
                    isActive={isActiveMenu === 'tierLogs'}
                    logs={tierLogs}
                    venueTypes={venueTypes}
                />
            </div>
        </div>
    );
};

export default Pricing;
